import { ApiFormType } from "@/types/forms/form.types.ts";
import { Modal, ModalProps, Button, ButtonLink, Stack } from "@skailer-com/uikit";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FormResultForEmployeeModalContent } from "@/components/form-result/result-modal/components/FormResultForEmployeeModalContent.tsx";
import { apiScoreCard } from "@/api/recruit/scorecard.api.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import { useSurveyParticipantResults } from "@/queries/surveys/use-surveys.query.ts";

interface Props extends ModalProps {
  questionnaireId: number;
  employeeId: number;
  type: ApiFormType;
  onOpenModal?: (questionnaireId: number) => void;
  onCloseModal?: (questionnaireId: number) => void;
}

export function FormResultForEmployeeModal({
  questionnaireId,
  employeeId,
  type,
  onOpenModal,
  onCloseModal,
  ...props
}: Props) {
  const { t } = useTranslation();
  const isSurvey = type === ApiFormType.survey;

  const { isExporting, downloadFile } = useDownloadHelper();
  const { data: surveyParticipantResults } = useSurveyParticipantResults(
    questionnaireId,
    employeeId,
    isSurvey && !!questionnaireId && !!employeeId
  );
  const canViewSurveyResults = !!surveyParticipantResults?.acl?.can_view;
  const hasActions = type === ApiFormType.scoreCard || (isSurvey && canViewSurveyResults);

  const handleDownloadFeedback = useCallback(() => {
    if (!employeeId) return;
    downloadFile({
      apiCall: () => apiScoreCard.downloadFeedback(employeeId, questionnaireId),
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.scoreCards.feedbackDownloaded, {
          [analyticProperties.id]: questionnaireId,
        });
      },
    });
  }, [employeeId, questionnaireId]);

  useEffect(() => {
    if (onOpenModal) onOpenModal(questionnaireId);

    return () => {
      if (onCloseModal) onCloseModal(questionnaireId);
    };
  }, []);
  return (
    <Modal
      {...props}
      layout={"stretch"}
      size={"lg"}
      variant={"side"}
      title={t("common:view_responses")}
      withCloser
      closeByEscEnabled
      actions={
        hasActions ? (
          <Stack className={"pt-(--gap-md) print:hidden"} gap={"md"}>
            {isSurvey && canViewSurveyResults && (
              <ButtonLink
                size={"lg"}
                variant={"secondary"}
                href={`/surveys/${questionnaireId}/view/summary`}
                target={"_blank"}
              >
                {t("common:view_all_responses")}
              </ButtonLink>
            )}

            {type === ApiFormType.scoreCard && (
              <Button
                size={"lg"}
                type={"submit"}
                isLoading={isExporting}
                onClick={() => handleDownloadFeedback()}
              >
                {t("common:download_pdf")}
              </Button>
            )}
          </Stack>
        ) : undefined
      }
    >
      <FormResultForEmployeeModalContent
        questionnaireId={questionnaireId}
        employeeId={employeeId}
        type={type}
      />
    </Modal>
  );
}
